import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import {
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  Copy,
  Check,
  AlertTriangle,
  CheckCircle2,
  BellRing,
} from 'lucide-react';
import { adminApi } from '../../services/api';
import type { ErrorLogItem, ErrorLogStats, ErrorLogSource } from '../../types';

type Filters = {
  source?: string;
  severity?: string;
  resolved?: 'any' | 'true' | 'false';
  provider?: string;
  errorClass?: string;
  sessionId?: string;
  traceId?: string;
  search?: string;
  from?: string;
  to?: string;
};

const SOURCE_OPTIONS: Array<{ value: ErrorLogSource; label: string }> = [
  { value: 'ai_stream', label: 'AI stream' },
  { value: 'ai_stream_aggregate', label: 'AI stream (aggregate)' },
  { value: 'ai_request', label: 'AI request' },
  { value: 'unhandled_rejection', label: 'Unhandled rejection' },
  { value: 'uncaught_exception', label: 'Uncaught exception' },
];

const SOURCE_LABELS: Record<string, string> = SOURCE_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {}
);

const inputCls =
  'px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full';
const labelCls = 'block text-sm font-medium text-gray-700 mb-1';

function prettyJson(value: any): string {
  if (value === undefined || value === null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col min-w-[200px]">
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="ml-1 text-gray-400 hover:text-gray-600"
      title="Copy"
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-md mb-2">
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span>{title}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="px-4 pb-4 pt-2">{children}</div>}
    </div>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const styles: Record<string, string> = {
    fatal: 'bg-red-100 text-red-800 border-red-200',
    error: 'bg-orange-100 text-orange-800 border-orange-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs font-medium rounded border ${
        styles[severity] || 'bg-gray-100 text-gray-700 border-gray-200'
      }`}
    >
      {severity}
    </span>
  );
}

const ErrorLogs: React.FC = () => {
  const [logs, setLogs] = useState<ErrorLogItem[]>([]);
  const [stats, setStats] = useState<ErrorLogStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<Filters>({ resolved: 'any' });
  const [selected, setSelected] = useState<ErrorLogItem | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const buildParams = useCallback(
    (p: number) => ({
      page: p,
      limit,
      source: filters.source || undefined,
      severity: filters.severity || undefined,
      provider: filters.provider || undefined,
      errorClass: filters.errorClass || undefined,
      sessionId: filters.sessionId || undefined,
      traceId: filters.traceId || undefined,
      search: filters.search || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      resolved:
        filters.resolved === 'any' || !filters.resolved ? undefined : filters.resolved === 'true',
    }),
    [filters, limit]
  );

  const fetchLogs = useCallback(
    async (p = page) => {
      setLoading(true);
      try {
        const res = await adminApi.listErrorLogs(buildParams(p));
        const data = res.data.data;
        setLogs(data?.logs || []);
        setTotal(data?.pagination?.total || 0);
        setPage(p);
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Failed to load error logs');
      } finally {
        setLoading(false);
      }
    },
    [buildParams, page]
  );

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminApi.getErrorLogStats(24);
      setStats(res.data.data || null);
    } catch {
      // Stats are supplementary — a failure here should not block the table.
    }
  }, []);

  useEffect(() => {
    fetchLogs(1);
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchLogs(1);
    fetchStats();
  };

  const resetFilters = () => {
    setFilters({ resolved: 'any' });
    setTimeout(() => fetchLogs(1), 0);
  };

  const openDetail = (log: ErrorLogItem) => {
    setSelected(log);
    setNotesDraft(log.notes || '');
  };

  const saveLog = async (resolved?: boolean) => {
    if (!selected) return;
    setSaving(true);
    try {
      const payload: { resolved?: boolean; notes?: string } = { notes: notesDraft };
      if (resolved !== undefined) payload.resolved = resolved;
      const res = await adminApi.updateErrorLog(selected._id, payload);
      const updated = res.data.data;
      if (updated) {
        setSelected(updated);
        setLogs((prev) => prev.map((l) => (l._id === updated._id ? updated : l)));
      }
      toast.success('Error log updated');
      fetchStats();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update error log');
    } finally {
      setSaving(false);
    }
  };

  const pages = Math.ceil(total / limit) || 1;

  return (
    <div className="p-6">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Error Logs</h1>
            <p className="text-sm text-gray-500 mt-1">
              Backend failures captured from the AI pipeline and process-level handlers.
            </p>
          </div>
          <button
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
            onClick={() => {
              fetchLogs(page);
              fetchStats();
            }}
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 uppercase tracking-wide">
                Distinct errors (24h)
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1">{stats.totals.groups}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 uppercase tracking-wide">
                Total occurrences (24h)
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1">
                {stats.totals.occurrences}
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Unresolved</div>
              <div className="text-2xl font-semibold text-orange-600 mt-1">
                {stats.totals.unresolved}
              </div>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 text-sm text-blue-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-blue-500" />
          <div>
            <span className="font-medium">
              Identical failures are grouped and counted rather than duplicated.
            </span>
            <span className="ml-1">
              The <code className="bg-blue-100 px-1 rounded">occurrences</code> column shows how
              often a fingerprint repeated. Logs are retained for a limited time (default 30 days).
            </span>
          </div>
        </div>

        {/* Filters */}
        <form onSubmit={handleSearch} className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <div className="flex flex-wrap gap-4 mb-3">
            <FieldGroup label="Source">
              <select
                className={inputCls}
                value={filters.source || ''}
                onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
              >
                <option value="">Any</option>
                {SOURCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FieldGroup>

            <FieldGroup label="Severity">
              <select
                className={inputCls}
                value={filters.severity || ''}
                onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value }))}
              >
                <option value="">Any</option>
                <option value="fatal">Fatal</option>
                <option value="error">Error</option>
                <option value="warning">Warning</option>
              </select>
            </FieldGroup>

            <FieldGroup label="Status">
              <select
                className={inputCls}
                value={filters.resolved || 'any'}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, resolved: e.target.value as Filters['resolved'] }))
                }
              >
                <option value="any">Any</option>
                <option value="false">Unresolved</option>
                <option value="true">Resolved</option>
              </select>
            </FieldGroup>

            <FieldGroup label="Error class">
              <input
                className={inputCls}
                placeholder="e.g. GoogleGenerativeAIError"
                value={filters.errorClass || ''}
                onChange={(e) => setFilters((f) => ({ ...f, errorClass: e.target.value }))}
              />
            </FieldGroup>

            <FieldGroup label="Message contains">
              <input
                className={inputCls}
                placeholder="e.g. Failed to parse stream"
                value={filters.search || ''}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              />
            </FieldGroup>

            <FieldGroup label="Session">
              <input
                className={inputCls}
                placeholder="sessionId"
                value={filters.sessionId || ''}
                onChange={(e) => setFilters((f) => ({ ...f, sessionId: e.target.value }))}
              />
            </FieldGroup>

            <FieldGroup label="From">
              <input
                type="datetime-local"
                className={inputCls}
                value={filters.from || ''}
                onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              />
            </FieldGroup>

            <FieldGroup label="To">
              <input
                type="datetime-local"
                className={inputCls}
                value={filters.to || ''}
                onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              />
            </FieldGroup>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="submit"
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Search
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              onClick={resetFilters}
            >
              Clear filters
            </button>
            <button
              type="button"
              className="px-3 py-2 text-xs border border-red-300 text-red-600 rounded-md hover:bg-red-50"
              onClick={() => {
                setFilters({ resolved: 'false', source: 'ai_stream' });
                setTimeout(() => fetchLogs(1), 0);
              }}
            >
              Unresolved stream failures
            </button>
          </div>
        </form>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Last seen</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Severity</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Source</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Error</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Model</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Count</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && logs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      No error logs match these filters.
                    </td>
                  </tr>
                )}
                {!loading &&
                  logs.map((log) => (
                    <tr
                      key={log._id}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => openDetail(log)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                        {dayjs(log.lastSeenAt).format('DD MMM YYYY HH:mm:ss')}
                      </td>
                      <td className="px-4 py-3">
                        <SeverityBadge severity={log.severity} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                        {SOURCE_LABELS[log.source] || log.source}
                      </td>
                      <td className="px-4 py-3 max-w-[420px]">
                        <div className="font-medium text-gray-900 truncate" title={log.message}>
                          {log.message}
                        </div>
                        {log.errorClass && (
                          <div className="text-xs text-gray-500 truncate">{log.errorClass}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {log.model || '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {log.occurrences}
                      </td>
                      <td className="px-4 py-3">
                        {log.resolved ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700">
                            <CheckCircle2 size={14} /> Resolved
                          </span>
                        ) : (
                          <span className="text-xs text-orange-600">Open</span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <span className="text-sm text-gray-500">
              {total} error{total === 1 ? '' : 's'} · page {page} of {pages}
            </span>
            <div className="flex items-center gap-1">
              <button
                className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                disabled={page <= 1 || loading}
                onClick={() => fetchLogs(page - 1)}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                disabled={page >= pages || loading}
                onClick={() => fetchLogs(page + 1)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)} />
          <div className="relative bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-start justify-between">
              <div className="pr-4">
                <div className="flex items-center gap-2 mb-1">
                  <SeverityBadge severity={selected.severity} />
                  <span className="text-xs text-gray-500">
                    {SOURCE_LABELS[selected.source] || selected.source}
                  </span>
                </div>
                <h2 className="text-lg font-semibold text-gray-900 break-words">
                  {selected.message}
                </h2>
              </div>
              <button className="text-gray-400 hover:text-gray-600" onClick={() => setSelected(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="px-6 py-4">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm mb-4">
                <div>
                  <dt className="text-gray-500">Error class</dt>
                  <dd className="text-gray-900">{selected.errorClass || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Occurrences</dt>
                  <dd className="text-gray-900">{selected.occurrences}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">First seen</dt>
                  <dd className="text-gray-900">
                    {dayjs(selected.firstSeenAt).format('DD MMM YYYY HH:mm:ss')}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Last seen</dt>
                  <dd className="text-gray-900">
                    {dayjs(selected.lastSeenAt).format('DD MMM YYYY HH:mm:ss')}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">HTTP status</dt>
                  <dd className="text-gray-900">
                    {selected.status ?? '—'} {selected.statusText || ''}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Code</dt>
                  <dd className="text-gray-900">{selected.code || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Provider / model</dt>
                  <dd className="text-gray-900">
                    {selected.provider || '—'} / {selected.model || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Operation</dt>
                  <dd className="text-gray-900">{selected.operation || '—'}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-gray-500">WhatsApp alert</dt>
                  <dd className="text-gray-900">
                    {selected.lastAlertedAt ? (
                      <span className="inline-flex items-center gap-1 text-green-700">
                        <BellRing size={14} />
                        Sent {dayjs(selected.lastAlertedAt).format('DD MMM YYYY HH:mm')}
                        {selected.alertCount && selected.alertCount > 1
                          ? ` · ${selected.alertCount} alerts`
                          : ''}
                      </span>
                    ) : (
                      <span className="text-gray-500">
                        Not alerted{' '}
                        <span className="text-xs">
                          (warnings never alert; repeats are throttled)
                        </span>
                      </span>
                    )}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-gray-500">Session</dt>
                  <dd className="text-gray-900 font-mono text-xs break-all">
                    {selected.sessionId || '—'}
                    {selected.sessionId && <CopyButton text={selected.sessionId} />}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-gray-500">Trace</dt>
                  <dd className="text-gray-900 font-mono text-xs break-all">
                    {selected.traceId || '—'}
                    {selected.traceId && <CopyButton text={selected.traceId} />}
                  </dd>
                </div>
              </dl>

              {selected.diagnostics && Object.keys(selected.diagnostics).length > 0 && (
                <CollapsibleSection title="Diagnostics" defaultOpen>
                  <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap break-words">
                    {prettyJson(selected.diagnostics)}
                  </pre>
                </CollapsibleSection>
              )}

              {selected.errorDetails && (
                <CollapsibleSection title="Error details">
                  <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap break-words">
                    {prettyJson(selected.errorDetails)}
                  </pre>
                </CollapsibleSection>
              )}

              {selected.cause && (
                <CollapsibleSection title="Cause">
                  <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap break-words">
                    {selected.cause}
                  </pre>
                </CollapsibleSection>
              )}

              {selected.stack && (
                <CollapsibleSection title="Stack trace">
                  <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap break-words">
                    {selected.stack}
                  </pre>
                </CollapsibleSection>
              )}

              {/* Triage */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <label className={labelCls}>Notes</label>
                <textarea
                  className={`${inputCls} h-24 resize-y`}
                  placeholder="What did you find? What was done about it?"
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                />
                {selected.resolved && selected.resolvedBy && (
                  <p className="text-xs text-gray-500 mt-1">
                    Resolved by {selected.resolvedBy}
                    {selected.resolvedAt
                      ? ` on ${dayjs(selected.resolvedAt).format('DD MMM YYYY HH:mm')}`
                      : ''}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-3">
                  <button
                    className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    disabled={saving}
                    onClick={() => saveLog()}
                  >
                    Save notes
                  </button>
                  {selected.resolved ? (
                    <button
                      className="px-4 py-2 text-sm border border-orange-300 text-orange-700 rounded-md hover:bg-orange-50 disabled:opacity-50"
                      disabled={saving}
                      onClick={() => saveLog(false)}
                    >
                      Reopen
                    </button>
                  ) : (
                    <button
                      className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      disabled={saving}
                      onClick={() => saveLog(true)}
                    >
                      Mark resolved
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorLogs;
